import { useEffect, useState } from "react";

/**
 * ARE SIGN-UPS OPEN, AND WHERE DOES SOMEONE ASK.
 *
 * The apex gets this by server-side substitution — `home()` writes `__SIGNUPS_OPEN__` and
 * `__CONTACT__` into the page as it serves it, so the legacy app always renders the truth. This
 * client is a static bundle on a CDN with no server in its render path, so it has to ask:
 * `GET /api/signups`.
 *
 * WHY NOT A CONSTANT. `flags.ts` argues, correctly, that a compliance gate should need a reviewed
 * diff rather than a dashboard toggle. This is the opposite kind of fact: it is not a decision this
 * repo makes, it is a *report* of the server's current state, and it has to be able to change
 * without a deploy. Hardcoding it would put the landing page and the API one variable-flip away
 * from contradicting each other — which is exactly what `cma/constants.ts` did to the persona
 * registry, and it took a rename shipping as a no-op before anyone noticed.
 *
 * THREE-VALUED ON PURPOSE, for the reason `useSession` gives about its own status: "unknown" is the
 * first paint, before the round trip has answered, and it is NOT the same as "closed". A CTA that
 * renders its closed state during that window flashes "we are not accepting sign-ups" at a visitor
 * for 200ms and then changes its mind. Callers render the neutral shape while unknown.
 *
 * A FAILED REQUEST RESOLVES TO CLOSED, NOT OPEN. If this endpoint cannot be reached, the honest
 * default is the one that does not invite someone into a form the API will refuse. The cost of
 * being wrong that way is a visitor who emails instead of signing up; the cost of the other way is
 * the defect this whole change removed.
 */
export type SignupsStatus = "unknown" | "open" | "closed";

export interface Signups {
  status: SignupsStatus;
  /** The address to write to for access. Server-owned; never hardcoded in this client. */
  contact: string | null;
}

interface SignupsShape {
  open?: boolean;
  contact?: string | null;
}

export function useSignups(): Signups {
  const [state, setState] = useState<Signups>({ status: "unknown", contact: null });

  useEffect(() => {
    let cancelled = false;
    // Deliberately a bare fetch and not `apiFetch`: this is a public endpoint on a public page, and
    // apiFetch's 401 handler redirects to /login, which would be absurd here.
    fetch("/api/signups", { credentials: "same-origin" })
      .then((r) => (r.ok ? (r.json() as Promise<SignupsShape>) : Promise.reject(new Error(String(r.status)))))
      .then((body) => {
        if (cancelled) return;
        setState({ status: body.open ? "open" : "closed", contact: body.contact ?? null });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "closed", contact: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * The one sentence a visitor reads about access, assembled from the server's own contact address.
 *
 * It is the API's refusal text from `/api/auth/signup`, word for word, and the apex page shows the
 * identical string. Three surfaces, one sentence: the page, the API and this client cannot say
 * three different things about who may create an account.
 */
export function privatePilotSentence(contact: string | null): string {
  const where = contact ? `contact ${contact}` : "get in touch";
  return `ValuBrief is in private pilot and not accepting new sign-ups yet. To request access, ${where}.`;
}
